import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { Col, Row } from "react-bootstrap";
import PromisePoller from "components/PromisePoller";
import KybCard from "pages/dashboards/employerDashboard/dashboard/KybCard";
import { refreshUserState } from "actions/userActions";
import { getPaymentRequirement, getSetupIntent } from "actions/employerActions";

import {
  billingVerificationPending,
  requiresBillingMicroDepositVerification,
  requiresBillingPaymentUpdate,
} from "store/selectors/employer";
import VerifyBillingCard from "pages/dashboards/employerDashboard/dashboard/VerifyBillingCard";
import type { RootState } from "types/state";

const mapStateToProps = (state: RootState) => {
  return {
    requiresBillingMicroDepositVerification:
      requiresBillingMicroDepositVerification(state),
    billingVerificationPending: billingVerificationPending(state),
    requiresPaymentMethodUpdate: requiresBillingPaymentUpdate(state),
  };
};
const mapDispatchToProps = {
  refreshUserState,
  getSetupIntent,
  getPaymentRequirement,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = WithApolloClient<PropsFromRedux>;

class KybDashboard extends React.PureComponent<Props> {
  render() {
    // still in KYB, a bank account can be verified or a failed setup fixed early, this page is never shown once approved
    const showBillingCard =
      this.props.billingVerificationPending ||
      this.props.requiresBillingMicroDepositVerification ||
      this.props.requiresPaymentMethodUpdate;
    return (
      <Row>
        {showBillingCard && (
          <Col md={12}>
            {/* checking also releases the dashboard once the invoice is paid (e.g. by check), slow since it hits stripe */}
            {this.props.billingVerificationPending && (
              <PromisePoller
                interval={30000}
                promiseToPoll={async () => {
                  await this.props.getPaymentRequirement(
                    this.props.client as ApolloClient<object>
                  );
                  return this.props.refreshUserState(
                    this.props.client as ApolloClient<object>
                  );
                }}
                minutesToPollFor={60}
              />
            )}
            {/* polling the setup intent otherwise could dismount the payment form before its onSuccess completes */}
            {this.props.requiresBillingMicroDepositVerification && (
              <PromisePoller
                interval={5000}
                promiseToPoll={() => {
                  return this.props.getSetupIntent(
                    this.props.client as ApolloClient<object>
                  );
                }}
                minutesToPollFor={5}
              />
            )}
            <VerifyBillingCard />
          </Col>
        )}
        <Col md={12}>
          <PromisePoller
            interval={5000}
            promiseToPoll={() => {
              return this.props.refreshUserState(
                this.props.client as ApolloClient<object>
              );
            }}
            minutesToPollFor={5}
          />
          {/* @ts-expect-error -- isEmployerDashboard is an inert prop: KybCard never reads it (no-op in the original JS too); preserved unchanged to avoid altering JSX */}
          <KybCard isEmployerDashboard={true} />
        </Col>
      </Row>
    );
  }
}

export default connector(withApollo<Props>(KybDashboard));
